import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronLeft, ChevronUp, ChevronDown, Plus, Trash2, Save,
  BookOpen, FileText, Video, ImageIcon, FileIcon, Eye, EyeOff,
  GripVertical, X, Upload, Loader2, Pencil, Presentation, Headphones
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../lib/api';
import RichTextEditor from './RichTextEditor';


/* ─── Confirm Dialog ─── */
const ConfirmDialog = ({ open, title, message, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="confirm-dialog">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl">
        <h3 className="font-heading text-lg font-semibold text-text-main mb-2">{title}</h3>
        <p className="text-sm text-text-muted mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-text-muted hover:bg-gray-100 rounded-lg"
            data-testid="confirm-cancel">Cancel</button>
          <button onClick={onConfirm} className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600"
            data-testid="confirm-delete">Delete</button>
        </div>
      </div>
    </div>
  );
};

/* ═══ COURSE BUILDER PAGE ═══ */
/* ─── Optional heading + short intro shown above a media block on the lesson page ─── */
const MEDIA_KINDS = ['audio', 'video', 'slides', 'pdf'];
const MediaIntroFields = ({ kind, noun, values, onChange }) => (
  <div className="mb-3 space-y-2" data-testid={`cb-${kind}-intro`}>
    <div>
      <label htmlFor={`cb-${kind}-title`} className="block text-[11px] font-medium text-text-muted mb-1">
        Heading above this {noun} (optional)
      </label>
      <input id={`cb-${kind}-title`} maxLength={120}
        value={values[`content_${kind}_title`] || ''}
        onChange={e => onChange(`content_${kind}_title`, e.target.value)}
        className="w-full px-3 py-2 text-sm bg-white rounded-lg border border-primary/20 focus:ring-2 focus:ring-primary"
        data-testid={`cb-${kind}-title`} />
    </div>
    <div>
      <label htmlFor={`cb-${kind}-description`} className="block text-[11px] font-medium text-text-muted mb-1">
        Short introduction (optional)
      </label>
      <textarea id={`cb-${kind}-description`} rows={2} maxLength={600}
        value={values[`content_${kind}_description`] || ''}
        onChange={e => onChange(`content_${kind}_description`, e.target.value)}
        className="w-full px-3 py-2 text-sm bg-white rounded-lg border border-primary/20 focus:ring-2 focus:ring-primary"
        data-testid={`cb-${kind}-description`} />
    </div>
  </div>
);

const CourseBuilder = ({ onBack }) => {
  const { profile } = useAuth();
  const isAdmin = profile?.is_admin === true;

  const [spaces, setSpaces] = useState([]);
  const [selectedSpaceId, setSelectedSpaceId] = useState(null);
  const [modules, setModules] = useState([]);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [lessonData, setLessonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null); // null | 'image' | 'pdf' | 'audio'
  const [message, setMessage] = useState(null);

  // Module add/edit
  const [addingModule, setAddingModule] = useState(false);
  const [moduleForm, setModuleForm] = useState({ title: '', description: '' });
  const [editingModuleId, setEditingModuleId] = useState(null);
  const [editModuleForm, setEditModuleForm] = useState({ title: '', description: '' });

  // Lesson add
  const [addingLesson, setAddingLesson] = useState(false);
  const [lessonForm, setLessonForm] = useState({ title: '', description: '', content_type: 'text' });

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  // Lesson editor fields — each content type is fully independent
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editText, setEditText] = useState('');
  const [editImageUrl, setEditImageUrl] = useState('');
  const [editVideoUrl, setEditVideoUrl] = useState('');
  const [editPdfUrl, setEditPdfUrl] = useState('');
  const [editAudioUrl, setEditAudioUrl] = useState('');
  const [editSlidesUrl, setEditSlidesUrl] = useState('');
  const [editPublished, setEditPublished] = useState(false);
  const [editComingSoon, setEditComingSoon] = useState(false);
  const [mediaIntro, setMediaIntro] = useState({});
  const setIntro = (key, value) => setMediaIntro(prev => ({ ...prev, [key]: value }));

  // Load spaces
  useEffect(() => {
    api.getGroups().then(r => {
      const allSpaces = r.data || [];
      setSpaces(allSpaces);
      if (allSpaces.length > 0) setSelectedSpaceId(allSpaces[0].id);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Load modules when space changes
  const fetchModules = useCallback(async () => {
    if (!selectedSpaceId) return;
    try {
      const res = await api.getSpaceModules(selectedSpaceId, null, true);
      setModules(res.data || []);
    } catch { /* ignore */ }
  }, [selectedSpaceId]);

  useEffect(() => {
    setSelectedModuleId(null);
    setSelectedLessonId(null);
    setLessonData(null);
    fetchModules();
  }, [fetchModules]);

  // Load lesson data when lesson selected
  useEffect(() => {
    if (!selectedLessonId) { setLessonData(null); return; }
    api.getLesson(selectedLessonId).then(r => {
      const d = r.data;
      setLessonData(d);
      setEditTitle(d.title || '');
      setEditDesc(d.description || '');
      setEditText(d.content_text || '');
      // Populate each URL from its dedicated column, fall back to legacy content_url for old records
      const legacyUrl = d.content_url || '';
      const t = d.content_type || '';
      setEditImageUrl(d.content_image_url || (t === 'image' ? legacyUrl : ''));
      setEditVideoUrl(d.content_video_url || (t === 'video' ? legacyUrl : ''));
      setEditPdfUrl(d.content_pdf_url   || (t === 'pdf'   ? legacyUrl : ''));
      setEditAudioUrl(d.content_audio_url || (t === 'audio' ? legacyUrl : ''));
      setEditSlidesUrl(d.content_slides_url || (t === 'slides' ? legacyUrl : ''));
      setEditPublished(d.is_published !== false);
      setEditComingSoon(!!d.is_coming_soon);
      const intro = {};
      MEDIA_KINDS.forEach(k => { intro[`content_${k}_title`] = d[`content_${k}_title`] || ''; intro[`content_${k}_description`] = d[`content_${k}_description`] || ''; });
      setMediaIntro(intro);
    }).catch(() => {});
  }, [selectedLessonId]);

  const selectedModule = modules.find(m => m.id === selectedModuleId);
  const lessons = selectedModule?.lessons || [];

  // ─── Module CRUD ───
  const handleAddModule = async () => {
    if (!moduleForm.title.trim()) return;
    setSaving(true);
    await api.createModule({
      space_id: selectedSpaceId,
      title: moduleForm.title.trim(),
      description: moduleForm.description.trim(),
      order_index: modules.length + 1,
    });
    setModuleForm({ title: '', description: '' });
    setAddingModule(false);
    await fetchModules();
    setSaving(false);
  };

  const handleSaveModuleEdit = async () => {
    if (!editModuleForm.title.trim()) return;
    setSaving(true);
    await api.updateModule(editingModuleId, {
      title: editModuleForm.title.trim(),
      description: editModuleForm.description.trim(),
    });
    setEditingModuleId(null);
    await fetchModules();
    setSaving(false);
  };

  const handleDeleteModule = (mod) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Module',
      message: `Are you sure you want to delete "${mod.title}"? All ${mod.lessons?.length || 0} lessons inside will also be permanently deleted.`,
      onConfirm: async () => {
        setConfirmDialog({ open: false });
        setSaving(true);
        try {
          await api.deleteModule(mod.id);
          if (selectedModuleId === mod.id) {
            setSelectedModuleId(null);
            setSelectedLessonId(null);
          }
          setMessage({ type: 'success', text: 'Module deleted successfully.' });
        } catch (err) {
          const detail = err?.response?.data?.detail || 'Failed to delete module.';
          setMessage({ type: 'error', text: detail });
        }
        await fetchModules();
        setSaving(false);
      },
    });
  };

  const handleReorderModule = async (moduleId, direction) => {
    const idx = modules.findIndex(m => m.id === moduleId);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= modules.length) return;
    const items = [
      { id: modules[idx].id, order_index: modules[swapIdx].order_index },
      { id: modules[swapIdx].id, order_index: modules[idx].order_index },
    ];
    await api.reorderModules(items);
    await fetchModules();
  };

  // ─── Lesson CRUD ───
  const handleAddLesson = async () => {
    if (!lessonForm.title.trim() || !selectedModuleId) return;
    setSaving(true);
    await api.createLesson({
      module_id: selectedModuleId,
      space_id: selectedSpaceId,
      title: lessonForm.title.trim(),
      description: lessonForm.description.trim(),
      content_type: lessonForm.content_type,
      order_index: lessons.length + 1,
    });
    setLessonForm({ title: '', description: '', content_type: 'text' });
    setAddingLesson(false);
    await fetchModules();
    setSaving(false);
  };

  const handleSaveLesson = async () => {
    if (!selectedLessonId || !editTitle.trim()) return;
    setSaving(true);
    // Derive a primary content_type for the lesson list display
    const activeTypes = [editText && 'text', editImageUrl && 'image', editVideoUrl && 'video',
      editPdfUrl && 'pdf', editAudioUrl && 'audio', editSlidesUrl && 'slides'].filter(Boolean);
    const primaryType = activeTypes.length > 1 ? 'multi' : activeTypes[0] || 'text';
    await api.updateLesson(selectedLessonId, {
      title: editTitle.trim(),
      description: editDesc.trim(),
      content_type: primaryType,
      content_text: editText || null,
      content_image_url: editImageUrl || null,
      content_video_url: editVideoUrl || null,
      content_pdf_url: editPdfUrl || null,
      content_audio_url: editAudioUrl || null,
      content_slides_url: editSlidesUrl || null,
      is_published: editPublished,
      is_coming_soon: editComingSoon,
      ...Object.fromEntries(Object.entries(mediaIntro).map(([k, v]) => [k, (v || '').trim() || null])),
    });
    await fetchModules();
    const r = await api.getLesson(selectedLessonId);
    setLessonData(r.data);
    setSaving(false);
    setMessage({ type: 'success', text: 'Lesson saved.' });
  };

  const handleDeleteLesson = (lesson) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Lesson',
      message: `Are you sure you want to delete "${lesson.title}"? This cannot be undone.`,
      onConfirm: async () => {
        setConfirmDialog({ open: false });
        setSaving(true);
        try {
          await api.deleteLesson(lesson.id);
          if (selectedLessonId === lesson.id) {
            setSelectedLessonId(null);
            setLessonData(null);
          }
          setMessage({ type: 'success', text: 'Lesson deleted successfully.' });
        } catch (err) {
          const detail = err?.response?.data?.detail || 'Failed to delete lesson.';
          setMessage({ type: 'error', text: detail });
        }
        await fetchModules();
        setSaving(false);
      },
    });
  };

  const handleReorderLesson = async (lessonId, direction) => {
    const idx = lessons.findIndex(l => l.id === lessonId);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= lessons.length) return;
    const items = [
      { id: lessons[idx].id, order_index: lessons[swapIdx].order_index || swapIdx + 1 },
      { id: lessons[swapIdx].id, order_index: lessons[idx].order_index || idx + 1 },
    ];
    await api.reorderLessons(items);
    await fetchModules();
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(type);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.courseBuilderUpload(formData);
      if (res.data?.url) {
        if (type === 'image') setEditImageUrl(res.data.url);
        else if (type === 'pdf') setEditPdfUrl(res.data.url);
        else if (type === 'audio') setEditAudioUrl(res.data.url);
        setMessage({ type: 'success', text: 'File uploaded successfully.' });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Upload failed.';
      setMessage({ type: 'error', text: detail });
    }
    setUploading(null);
  };

  // Access guard
  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-4 lg:p-8 text-center">
        <div className="glass-card rounded-xl p-8">
          <h2 className="font-heading text-xl font-semibold text-text-main mb-2">Access Denied</h2>
          <p className="text-text-muted mb-6">Admin access required.</p>
          <button onClick={onBack} className="px-6 py-2 bg-primary text-white rounded-lg" data-testid="cb-go-back">Go Back</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto p-4 lg:p-6 pb-24" data-testid="course-builder">
      <ConfirmDialog {...confirmDialog} onCancel={() => setConfirmDialog({ open: false })} />

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="p-2 hover:bg-primary/10 rounded-full" data-testid="cb-back-btn">
          <ChevronLeft className="w-6 h-6 text-primary" />
        </button>
        <div>
          <h1 className="font-heading text-2xl font-semibold text-primary" data-testid="cb-title">Course Builder</h1>
          <p className="text-sm text-text-muted">Manage modules and lessons for your spaces</p>
        </div>
        {saving && <Loader2 className="w-5 h-5 text-primary animate-spin ml-3" />}
      </div>

      {/* Message toast */}
      {message && (
        <div className={`mb-4 px-4 py-3 rounded-lg flex items-center justify-between text-sm ${
          message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`} data-testid="cb-message">
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)} className="ml-3 hover:opacity-70"><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="grid grid-cols-12 gap-5">
        {/* ═══ LEFT PANEL: Spaces + Modules ═══ */}
        <div className="col-span-12 lg:col-span-3">
          <div className="glass-card rounded-xl p-4 sticky top-24">
            {/* Space Selector */}
            <label className="text-xs font-medium text-text-muted mb-1.5 block">Space</label>
            <select value={selectedSpaceId || ''} onChange={e => setSelectedSpaceId(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-primary/20 bg-white text-sm text-text-main mb-2 focus:ring-2 focus:ring-primary"
              data-testid="cb-space-select">
              {spaces.map(s => (
                <option key={s.id} value={s.id}>{s.name}{s.is_coming_soon ? ' [Coming Soon]' : ' [Available]'}</option>
              ))}
            </select>

            {/* Space Visibility Toggle */}
            {selectedSpaceId && (() => {
              const sp = spaces.find(s => s.id === selectedSpaceId);
              if (!sp) return null;
              return (
                <div className="flex items-center justify-between mb-4 p-2.5 rounded-lg bg-primary/5">
                  <span className="text-xs text-text-muted">Visibility</span>
                  <button
                    onClick={async () => {
                      const newState = !sp.is_coming_soon;
                      setSaving(true);
                      await api.updateGroup(selectedSpaceId, { is_coming_soon: newState });
                      const res = await api.getGroups();
                      setSpaces(res.data || []);
                      setSaving(false);
                      setMessage({ type: 'success', text: `Space set to ${newState ? 'Coming Soon' : 'Available'}` });
                    }}
                    className={`px-3 py-1 rounded-full text-[11px] font-medium transition-colors ${
                      sp.is_coming_soon
                        ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                    data-testid="cb-visibility-toggle"
                  >
                    {sp.is_coming_soon ? 'Coming Soon' : 'Available'}
                  </button>
                </div>
              );
            })()}

            {/* Module List */}
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-text-main">Modules</h3>
              <button onClick={() => { setAddingModule(true); setModuleForm({ title: '', description: '' }); }}
                className="p-1.5 hover:bg-primary/10 rounded-lg text-primary" data-testid="cb-add-module-btn">
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {/* Add Module Form */}
            {addingModule && (
              <div className="p-3 bg-primary/5 rounded-lg mb-3 space-y-2" data-testid="cb-add-module-form">
                <input value={moduleForm.title} onChange={e => setModuleForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Module title" autoFocus
                  className="w-full px-3 py-2 text-sm bg-white rounded-lg border border-primary/20"
                  data-testid="cb-module-title-input" />
                <input value={moduleForm.description} onChange={e => setModuleForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Description (optional)"
                  className="w-full px-3 py-2 text-sm bg-white rounded-lg border border-primary/20"
                  data-testid="cb-module-desc-input" />
                <div className="flex gap-2">
                  <button onClick={handleAddModule} disabled={!moduleForm.title.trim() || saving}
                    className="px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-medium disabled:opacity-40 flex items-center gap-1"
                    data-testid="cb-save-new-module"><Save className="w-3 h-3" />Save</button>
                  <button onClick={() => setAddingModule(false)} className="px-3 py-1.5 text-text-muted text-xs">Cancel</button>
                </div>
              </div>
            )}

            {/* Module items */}
            <div className="space-y-1" data-testid="cb-module-list">
              {modules.map((mod, idx) => (
                <div key={mod.id}>
                  {editingModuleId === mod.id ? (
                    <div className="p-3 bg-primary/5 rounded-lg space-y-2" data-testid="cb-edit-module-form">
                      <input value={editModuleForm.title} onChange={e => setEditModuleForm(f => ({ ...f, title: e.target.value }))}
                        className="w-full px-3 py-2 text-sm bg-white rounded-lg border border-primary/20" autoFocus
                        onKeyDown={e => e.key === 'Enter' && handleSaveModuleEdit()}
                        data-testid="cb-edit-module-title" />
                      <input value={editModuleForm.description} onChange={e => setEditModuleForm(f => ({ ...f, description: e.target.value }))}
                        placeholder="Description" className="w-full px-3 py-2 text-sm bg-white rounded-lg border border-primary/20"
                        data-testid="cb-edit-module-desc" />
                      <div className="flex gap-2">
                        <button onClick={handleSaveModuleEdit} disabled={!editModuleForm.title.trim() || saving}
                          className="px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-medium disabled:opacity-40"
                          data-testid="cb-save-module-edit"><Save className="w-3 h-3 inline mr-1" />Save</button>
                        <button onClick={() => setEditingModuleId(null)} className="px-3 py-1.5 text-text-muted text-xs">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className={`group flex items-center gap-1 p-2.5 rounded-lg cursor-pointer transition-colors ${
                      selectedModuleId === mod.id ? 'bg-primary/10 border-l-3 border-primary' : 'hover:bg-primary/5'
                    }`}
                      onClick={() => { setSelectedModuleId(mod.id); setSelectedLessonId(null); setLessonData(null); }}
                      data-testid={`cb-module-${mod.id}`}>
                      <GripVertical className="w-3.5 h-3.5 text-text-muted/40 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-main truncate">{mod.title}</p>
                        <p className="text-[10px] text-text-muted">{mod.lessons?.length || 0} lessons</p>
                      </div>
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={e => { e.stopPropagation(); handleReorderModule(mod.id, 'up'); }}
                          disabled={idx === 0} className="p-1 hover:bg-primary/10 rounded disabled:opacity-20"
                          data-testid={`cb-module-up-${mod.id}`}><ChevronUp className="w-3.5 h-3.5 text-text-muted" /></button>
                        <button onClick={e => { e.stopPropagation(); handleReorderModule(mod.id, 'down'); }}
                          disabled={idx === modules.length - 1} className="p-1 hover:bg-primary/10 rounded disabled:opacity-20"
                          data-testid={`cb-module-down-${mod.id}`}><ChevronDown className="w-3.5 h-3.5 text-text-muted" /></button>
                        <button onClick={e => { e.stopPropagation(); setEditingModuleId(mod.id); setEditModuleForm({ title: mod.title, description: mod.description || '' }); }}
                          className="p-1 hover:bg-primary/10 rounded" data-testid={`cb-module-edit-${mod.id}`}>
                          <Pencil className="w-3.5 h-3.5 text-text-muted" /></button>
                        <button onClick={e => { e.stopPropagation(); handleDeleteModule(mod); }}
                          className="p-1 hover:bg-red-50 rounded" data-testid={`cb-module-delete-${mod.id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {modules.length === 0 && !addingModule && (
                <p className="text-xs text-text-muted text-center py-4">No modules yet. Click + to add one.</p>
              )}
            </div>
          </div>
        </div>

        {/* ═══ CENTER PANEL: Lessons ═══ */}
        <div className="col-span-12 lg:col-span-3">
          <div className="glass-card rounded-xl p-4 sticky top-24">
            {selectedModule ? (
              <>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-medium text-text-main">{selectedModule.title}</h3>
                    <p className="text-[10px] text-text-muted">{lessons.length} lessons</p>
                  </div>
                  <button onClick={() => { setAddingLesson(true); setLessonForm({ title: '', description: '', content_type: 'text' }); }}
                    className="p-1.5 hover:bg-primary/10 rounded-lg text-primary" data-testid="cb-add-lesson-btn">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                {/* Add Lesson Form */}
                {addingLesson && (
                  <div className="p-3 bg-primary/5 rounded-lg mb-3 space-y-2" data-testid="cb-add-lesson-form">
                    <input value={lessonForm.title} onChange={e => setLessonForm(f => ({ ...f, title: e.target.value }))}
                      placeholder="Lesson title" autoFocus
                      className="w-full px-3 py-2 text-sm bg-white rounded-lg border border-primary/20"
                      data-testid="cb-lesson-title-input" />
                    <input value={lessonForm.description} onChange={e => setLessonForm(f => ({ ...f, description: e.target.value }))}
                      placeholder="Description (optional)"
                      className="w-full px-3 py-2 text-sm bg-white rounded-lg border border-primary/20" />
                    <div className="flex gap-2">
                      <button onClick={handleAddLesson} disabled={!lessonForm.title.trim() || saving}
                        className="px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-medium disabled:opacity-40 flex items-center gap-1"
                        data-testid="cb-save-new-lesson"><Save className="w-3 h-3" />Save</button>
                      <button onClick={() => setAddingLesson(false)} className="px-3 py-1.5 text-text-muted text-xs">Cancel</button>
                    </div>
                  </div>
                )}

                {/* Lesson List */}
                <div className="space-y-1" data-testid="cb-lesson-list">
                  {lessons.map((lesson, idx) => (
                    <div key={lesson.id}
                      className={`group flex items-center gap-1 p-2.5 rounded-lg cursor-pointer transition-colors ${
                        selectedLessonId === lesson.id ? 'bg-primary/10 border-l-3 border-primary' : 'hover:bg-primary/5'
                      }`}
                      onClick={() => setSelectedLessonId(lesson.id)}
                      data-testid={`cb-lesson-${lesson.id}`}>
                      {/* Lesson item */}
                      <div className="flex-1 min-w-0 ml-1">
                        <p className="text-xs font-medium text-text-main truncate">{lesson.title}</p>
                        <p className="text-[10px] text-text-muted capitalize">{lesson.content_type || 'text'}</p>
                      </div>
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={e => { e.stopPropagation(); handleReorderLesson(lesson.id, 'up'); }}
                          disabled={idx === 0} className="p-1 hover:bg-primary/10 rounded disabled:opacity-20"
                          data-testid={`cb-lesson-up-${lesson.id}`}><ChevronUp className="w-3 h-3 text-text-muted" /></button>
                        <button onClick={e => { e.stopPropagation(); handleReorderLesson(lesson.id, 'down'); }}
                          disabled={idx === lessons.length - 1} className="p-1 hover:bg-primary/10 rounded disabled:opacity-20"
                          data-testid={`cb-lesson-down-${lesson.id}`}><ChevronDown className="w-3 h-3 text-text-muted" /></button>
                        <button onClick={e => { e.stopPropagation(); handleDeleteLesson(lesson); }}
                          className="p-1 hover:bg-red-50 rounded" data-testid={`cb-lesson-delete-${lesson.id}`}>
                          <Trash2 className="w-3 h-3 text-red-400" /></button>
                      </div>
                    </div>
                  ))}
                  {lessons.length === 0 && !addingLesson && (
                    <p className="text-xs text-text-muted text-center py-4">No lessons yet. Click + to add.</p>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <BookOpen className="w-10 h-10 text-primary/20 mx-auto mb-3" />
                <p className="text-sm text-text-muted">Select a module to see its lessons</p>
              </div>
            )}
          </div>
        </div>

        {/* ═══ RIGHT PANEL: Lesson Editor ═══ */}
        <div className="col-span-12 lg:col-span-6">
          {lessonData ? (
            <div className="glass-card rounded-xl p-6" data-testid="cb-lesson-editor">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-lg font-semibold text-text-main">Edit Lesson</h3>
                <div className="flex items-center gap-3">
                  {/* Publish toggle */}
                  <button onClick={() => setEditPublished(!editPublished)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      editPublished ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}
                    data-testid="cb-publish-toggle">
                    {editPublished ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    {editPublished ? 'Published' : 'Draft'}
                  </button>
                  {!editPublished && (
                    <button onClick={() => setEditComingSoon(!editComingSoon)}
                      aria-pressed={editComingSoon}
                      title="Members see this lesson's title with a Coming soon label, but no content"
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                        editComingSoon ? 'bg-primary/15 text-primary' : 'bg-gray-100 text-text-muted'
                      }`}
                      data-testid="cb-coming-soon-toggle">
                      {editComingSoon ? 'Coming soon: shown' : 'Coming soon: hidden'}
                    </button>
                  )}
                  <button onClick={() => { setSelectedLessonId(null); setLessonData(null); }}
                    className="p-1.5 hover:bg-primary/10 rounded-full" data-testid="cb-close-editor">
                    <X className="w-4 h-4 text-text-muted" />
                  </button>
                </div>
              </div>

              <div className="space-y-5">
                {/* Title */}
                <div>
                  <label className="text-xs font-medium text-text-muted mb-1 block">Title</label>
                  <input value={editTitle} onChange={e => setEditTitle(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm bg-white rounded-lg border border-primary/20 focus:ring-2 focus:ring-primary"
                    data-testid="cb-edit-title" />
                </div>

                {/* Description */}
                <div>
                  <label className="text-xs font-medium text-text-muted mb-1 block">Description</label>
                  <input value={editDesc} onChange={e => setEditDesc(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm bg-white rounded-lg border border-primary/20 focus:ring-2 focus:ring-primary"
                    data-testid="cb-edit-desc" />
                </div>

                {/* ── Content sections — each is fully independent ── */}
                <div className="space-y-6">
                  <p className="text-xs font-semibold text-text-muted uppercase tracking-wide">Content</p>

                  {/* Text (rich text) */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-3.5 h-3.5 text-primary" aria-hidden="true" />
                      <label className="text-xs font-medium text-text-main">Text (optional)</label>
                      {editText && (
                        <button type="button" onClick={() => setEditText('')}
                          className="ml-auto text-[10px] text-red-400 hover:text-red-600"
                          data-testid="cb-clear-text">Clear</button>
                      )}
                    </div>
                    <RichTextEditor content={editText} onChange={setEditText} />
                  </div>

                  <div className="border-t border-primary/10 pt-4">
                    <p className="text-xs text-text-muted mb-4">Add any combination of media below — each section is independent.</p>
                  </div>

                  {/* Image */}
                  <div className="p-4 rounded-lg bg-primary/[0.03] border border-primary/10">
                    <div className="flex items-center gap-2 mb-3">
                      <ImageIcon className="w-3.5 h-3.5 text-primary" aria-hidden="true" />
                      <span className="text-xs font-medium text-text-main">Image (optional)</span>
                      {editImageUrl && (
                        <button type="button" onClick={() => setEditImageUrl('')}
                          className="ml-auto text-[10px] text-red-400 hover:text-red-600"
                          data-testid="cb-clear-image">Remove</button>
                      )}
                    </div>
                    {editImageUrl && (
                      <div className="mb-3 p-3 bg-white rounded-lg">
                        <img src={editImageUrl} alt="Preview" className="max-h-48 rounded-lg object-contain" />
                      </div>
                    )}
                    <label className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg cursor-pointer transition-colors text-sm font-medium ${
                      uploading === 'image' ? 'bg-primary/20 text-primary/60' : 'bg-primary/10 hover:bg-primary/15 text-primary'
                    }`} data-testid="cb-image-upload">
                      <Upload className="w-4 h-4" />
                      {uploading === 'image' ? 'Uploading...' : 'Upload Image'}
                      <input type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={e => handleFileUpload(e, 'image')} className="hidden" disabled={!!uploading} />
                    </label>
                  </div>

                  {/* YouTube */}
                  <div className="p-4 rounded-lg bg-primary/[0.03] border border-primary/10">
                    <div className="flex items-center gap-2 mb-3">
                      <Video className="w-3.5 h-3.5 text-primary" aria-hidden="true" />
                      <span className="text-xs font-medium text-text-main">YouTube Video (optional)</span>
                      {editVideoUrl && (
                        <button type="button" onClick={() => setEditVideoUrl('')}
                          className="ml-auto text-[10px] text-red-400 hover:text-red-600"
                          data-testid="cb-clear-video">Remove</button>
                      )}
                    </div>
                    <MediaIntroFields kind="video" noun="video" values={mediaIntro} onChange={setIntro} />
                    <input value={editVideoUrl} onChange={e => setEditVideoUrl(e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..."
                      className="w-full px-3 py-2.5 text-sm bg-white rounded-lg border border-primary/20 focus:ring-2 focus:ring-primary"
                      data-testid="cb-video-url" />
                    {editVideoUrl && (
                      <div className="mt-3 aspect-video rounded-lg overflow-hidden bg-black">
                        <iframe
                          src={editVideoUrl.includes('youtube.com/watch')
                            ? editVideoUrl.replace('watch?v=', 'embed/').split('&')[0]
                            : editVideoUrl.includes('youtu.be/')
                              ? `https://www.youtube.com/embed/${editVideoUrl.split('youtu.be/')[1]?.split('?')[0]}`
                              : editVideoUrl}
                          className="w-full h-full" allowFullScreen title="Video preview"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
                      </div>
                    )}
                  </div>

                  {/* PDF */}
                  <div className="p-4 rounded-lg bg-primary/[0.03] border border-primary/10">
                    <div className="flex items-center gap-2 mb-3">
                      <FileIcon className="w-3.5 h-3.5 text-primary" aria-hidden="true" />
                      <span className="text-xs font-medium text-text-main">PDF / Document (optional)</span>
                      {editPdfUrl && (
                        <button type="button" onClick={() => setEditPdfUrl('')}
                          className="ml-auto text-[10px] text-red-400 hover:text-red-600"
                          data-testid="cb-clear-pdf">Remove</button>
                      )}
                    </div>
                    <MediaIntroFields kind="pdf" noun="document" values={mediaIntro} onChange={setIntro} />
                    {editPdfUrl && (
                      <div className="mb-3 p-3 bg-white rounded-lg">
                        <a href={editPdfUrl} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-primary hover:underline">
                          <FileIcon className="w-4 h-4" /> View uploaded file
                        </a>
                      </div>
                    )}
                    <label className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg cursor-pointer transition-colors text-sm font-medium ${
                      uploading === 'pdf' ? 'bg-primary/20 text-primary/60' : 'bg-primary/10 hover:bg-primary/15 text-primary'
                    }`} data-testid="cb-pdf-upload">
                      <Upload className="w-4 h-4" />
                      {uploading === 'pdf' ? 'Uploading...' : 'Upload PDF/Document'}
                      <input type="file" accept=".pdf,.doc,.docx"
                        onChange={e => handleFileUpload(e, 'pdf')} className="hidden" disabled={!!uploading} />
                    </label>
                  </div>

                  {/* Audio */}
                  <div className="p-4 rounded-lg bg-primary/[0.03] border border-primary/10">
                    <div className="flex items-center gap-2 mb-3">
                      <Headphones className="w-3.5 h-3.5 text-primary" aria-hidden="true" />
                      <span className="text-xs font-medium text-text-main">Audio (optional)</span>
                      {editAudioUrl && (
                        <button type="button" onClick={() => setEditAudioUrl('')}
                          className="ml-auto text-[10px] text-red-400 hover:text-red-600"
                          data-testid="cb-clear-audio">Remove</button>
                      )}
                    </div>
                    <MediaIntroFields kind="audio" noun="audio" values={mediaIntro} onChange={setIntro} />
                    {editAudioUrl && (
                      <div className="mb-3 p-4 bg-white rounded-lg">
                        <audio controls className="w-full" src={editAudioUrl}>Your browser does not support audio.</audio>
                      </div>
                    )}
                    <label className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg cursor-pointer transition-colors text-sm font-medium ${
                      uploading === 'audio' ? 'bg-primary/20 text-primary/60' : 'bg-primary/10 hover:bg-primary/15 text-primary'
                    }`} data-testid="cb-audio-upload">
                      <Upload className="w-4 h-4" />
                      {uploading === 'audio' ? 'Uploading...' : 'Upload Audio (MP3/M4A)'}
                      <input type="file" accept="audio/mpeg,audio/mp4,audio/x-m4a,audio/mp3,.mp3,.m4a"
                        onChange={e => handleFileUpload(e, 'audio')} className="hidden" disabled={!!uploading} />
                    </label>
                  </div>

                  {/* Slides */}
                  <div className="p-4 rounded-lg bg-primary/[0.03] border border-primary/10">
                    <div className="flex items-center gap-2 mb-3">
                      <Presentation className="w-3.5 h-3.5 text-primary" aria-hidden="true" />
                      <span className="text-xs font-medium text-text-main">Slides — Canva or Google Slides (optional)</span>
                      {editSlidesUrl && (
                        <button type="button" onClick={() => setEditSlidesUrl('')}
                          className="ml-auto text-[10px] text-red-400 hover:text-red-600"
                          data-testid="cb-clear-slides">Remove</button>
                      )}
                    </div>
                    <MediaIntroFields kind="slides" noun="slides" values={mediaIntro} onChange={setIntro} />
                    <input value={editSlidesUrl} onChange={e => setEditSlidesUrl(e.target.value)}
                      placeholder="https://www.canva.com/design/.../view or https://docs.google.com/presentation/d/.../embed"
                      className="w-full px-3 py-2.5 text-sm bg-white rounded-lg border border-primary/20 focus:ring-2 focus:ring-primary"
                      data-testid="cb-slides-url" />
                    <p className="text-[10px] text-text-muted mt-1">Paste a Canva share link or Google Slides embed URL.</p>
                    {editSlidesUrl && (
                      <div className="mt-3 aspect-video rounded-lg overflow-hidden border border-primary/10">
                        <iframe src={editSlidesUrl} className="w-full h-full" allowFullScreen title="Slides preview" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Save button */}
                <div className="flex justify-end pt-2">
                  <button onClick={handleSaveLesson} disabled={saving || !editTitle.trim()}
                    className="px-6 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 flex items-center gap-2 disabled:opacity-40 transition-colors"
                    data-testid="cb-save-lesson">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="glass-card rounded-xl p-8 text-center min-h-[400px] flex flex-col items-center justify-center">
              <BookOpen className="w-14 h-14 text-primary/15 mb-4" />
              <h3 className="font-heading text-lg font-medium text-text-main mb-2">
                {selectedModule ? 'Select a lesson to edit' : 'Select a module first'}
              </h3>
              <p className="text-sm text-text-muted max-w-sm">
                {selectedModule
                  ? 'Click on a lesson from the list to open it in the editor. You can also add new lessons or reorder existing ones.'
                  : 'Choose a space and module from the left panel to get started with course editing.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseBuilder;
